import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Info, FolderEdit, Tags, BookOpen } from "lucide-react";
import { CategoryManagerModal } from "./editor/CategoryManagerModal";
import { TagManagerModal } from "./editor/TagManagerModal";

interface BlogPost {
  slug: string;
  title_en: string;
  title_tr: string;
  date: string;
  description_en: string;
  description_tr: string;
  tags_en: string;
  tags_tr: string;
  content_en: string;
  content_tr: string;
  published?: number;
  categories?: string;
}

interface Category {
  id: string;
  name_en: string;
  name_tr: string;
}

interface Tag {
  id: string;
  name_en: string;
  name_tr: string;
}

interface AdminBlogTabProps {
  blogs: BlogPost[];
  categories: Category[];
  tags: Tag[];
  onRefresh: () => void;
}

interface BlogRowProps {
  blog: BlogPost;
  idx: number;
  onDelete: (slug: string) => void;
}

function BlogRow({ blog, idx, onDelete }: BlogRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 w-24 text-center select-none">
        {blog.published === 1 ? (
          <span className="inline-block text-[9px] font-bold text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded">
            Published
          </span>
        ) : (
          <span className="inline-block text-[9px] font-bold text-amber-500 bg-amber-500/10 px-1.5 py-0.5 rounded">
            Draft
          </span>
        )}
      </td>
      <td className="p-4 w-28 font-mono text-xs text-muted-foreground leading-snug">
        {blog.date}
      </td>
      <td className="p-4 w-36">
        <span className="inline-block text-[10px] text-brand-primary font-semibold font-mono bg-brand-primary/5 px-2 py-0.5 rounded animate-none">
          /{blog.slug}
        </span>
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <div className="font-semibold text-foreground leading-snug flex items-center gap-1.5">
          <BookOpen className="h-4 w-4 text-brand-primary/65 shrink-0" />
          <span className="truncate">{blog.title_en || blog.title_tr}</span>
        </div>
        {blog.title_en && blog.title_tr && blog.title_tr !== blog.title_en && (
          <p className="text-xs text-muted-foreground italic pl-5.5">
            {blog.title_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-md space-y-1">
        <p className="text-xs text-foreground/85 line-clamp-2 leading-relaxed">
          {blog.description_en || blog.description_tr}
        </p>
        {blog.description_en && blog.description_tr && blog.description_tr !== blog.description_en && (
          <p className="text-[11px] text-muted-foreground/80 italic line-clamp-2 leading-relaxed">
            {blog.description_tr}
          </p>
        )}
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Link to={`/blog/editor/${blog.slug}`}>
            <Button
              size="icon"
              variant="outline"
              className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
            >
              <Edit2 className="h-4 w-4" />
            </Button>
          </Link>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(blog.slug)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}

function BlogTable({ blogs, onDelete }: { blogs: BlogPost[], onDelete: (slug: string) => void }) {
  if (blogs.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No articles configured. Click Article Editor above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4 w-24 text-center">Status</th>
              <th className="p-4 w-28">Date</th>
              <th className="p-4 w-36">Slug</th>
              <th className="p-4">Title (EN / TR)</th>
              <th className="p-4">Description (EN / TR)</th>
              <th className="p-4 w-28 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {blogs.map((blog, idx) => (
              <BlogRow
                key={blog.slug}
                blog={blog}
                idx={idx}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminBlogTab({ blogs, categories, tags, onRefresh }: AdminBlogTabProps) {
  const [isCategoriesOpen, setIsCategoriesOpen] = useState(false);
  const [isTagsOpen, setIsTagsOpen] = useState(false);

  const handleDelete = async (slug: string) => {
    if (!confirm("Are you sure you want to delete this blog post?")) return;
    try {
      await adminFetch("/api/admin/blog", { operation: "delete", slug });
      toast.success("Blog post deleted successfully!");
      onRefresh();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete blog post");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center pb-2 border-b border-border/80 text-left gap-4">
        <div>
          <h4 className="text-xl font-bold text-foreground uppercase tracking-wider">Blog Management</h4>
          <p className="text-xs text-muted-foreground mt-0.5">Publish articles, upload .mdx files, and manage content</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setIsCategoriesOpen(true)}
            className="border-border/60 text-foreground hover:bg-muted/50"
          >
            <FolderEdit className="h-4 w-4 mr-2" /> Categories
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={() => setIsTagsOpen(true)}
            className="border-border/60 text-foreground hover:bg-muted/50"
          >
            <Tags className="h-4 w-4 mr-2" /> Tags
          </Button>

          <Link to="/blog/editor">
            <Button className="bg-brand-primary text-white">
              <Plus className="h-4 w-4 mr-2" /> Article Editor
            </Button>
          </Link>
        </div>
      </div>

      <div className="flex items-start gap-2.5 p-3 rounded-xl bg-brand-primary/5 dark:bg-brand-primary/10 border border-brand-primary/10 text-brand-primary text-xs max-w-3xl leading-relaxed text-left">
        <Info className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <strong>Medium-Like Article Editor:</strong> Click <strong>Article Editor</strong> to open the full-screen editor with Markdown support, categories & tags multiselection, and Cloudflare R2 bucket image uploads. Max blogs displayed on the public site is <strong>6 items</strong>.
        </div>
      </div>

      <div>
        <BlogTable blogs={blogs} onDelete={handleDelete} />
      </div>

      <CategoryManagerModal
        isOpen={isCategoriesOpen}
        onClose={() => setIsCategoriesOpen(false)}
        categories={categories}
        onRefresh={onRefresh}
      />

      <TagManagerModal
        isOpen={isTagsOpen}
        onClose={() => setIsTagsOpen(false)}
        tags={tags}
        onRefresh={onRefresh}
      />
    </div>
  );
}
export default AdminBlogTab;
